import { useEffect, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { ArrowLeft, Plus, Trash2, Calculator } from "lucide-react";
import { useForm, useFieldArray, Controller } from "react-hook-form";
import toast from "react-hot-toast";
import { Button, Input, Select, Textarea, Card } from "@/components/ui";
import { budgetsApi, clientsApi, productsApi } from "@/services/api";

interface BudgetItemForm {
  product_id: string;
  name: string;
  description: string;
  detailed_description: string;
  quantity: number;
  unit: string;
  unit_price: number;
  discount: number;
  discount_type: "percent" | "fixed";
  billing_type: string;
  hours_per_day: number;
  days_per_month: number;
  months: number;
}

interface BudgetFormData {
  client_id: string;
  title: string;
  description: string;
  valid_until: string;
  payment_terms: string;
  delivery_time: string;
  warranty: string;
  notes: string;
  internal_notes: string;
  discount: number;
  discount_type: "percent" | "fixed";
  items: BudgetItemForm[];
}

const DISCOUNT_TYPES = [
  { value: "percent", label: "%" },
  { value: "fixed", label: "R$" },
];

const BILLING_TYPES = [
  { value: "one_time", label: "Única" },
  { value: "hourly", label: "Por hora" },
  { value: "daily", label: "Diária" },
  { value: "weekly", label: "Semanal" },
  { value: "monthly", label: "Mensal" },
  { value: "quarterly", label: "Trimestral" },
  { value: "semiannual", label: "Semestral" },
  { value: "yearly", label: "Anual" },
  { value: "hourly_billed_daily", label: "Hora → Dia" },
  { value: "hourly_billed_monthly", label: "Hora → Mês" },
  { value: "daily_billed_monthly", label: "Dia → Mês" },
  { value: "monthly_billed_yearly", label: "Mês → Ano" },
  { value: "on_demand", label: "Sob demanda" },
];

// Função para calcular o total de um item baseado no tipo de cobrança
const calculateItemTotal = (
  item: BudgetItemForm,
): { total: number; breakdown: string } => {
  const basePrice = item.unit_price;
  const qty = item.quantity || 1;
  const hoursPerDay = item.hours_per_day || 8;
  const daysPerMonth = item.days_per_month || 22;
  const months = item.months || 1;

  let total = 0;
  let breakdown = "";

  switch (item.billing_type) {
    case "hourly":
      // Preço por hora × quantidade de horas
      total = basePrice * qty;
      breakdown = `${qty}h × R$${basePrice.toFixed(2)}/h`;
      break;

    case "daily":
      // Preço por dia × quantidade de dias
      total = basePrice * qty;
      breakdown = `${qty} dias × R$${basePrice.toFixed(2)}/dia`;
      break;

    case "weekly":
      // Preço por semana × quantidade de semanas
      total = basePrice * qty;
      breakdown = `${qty} sem × R$${basePrice.toFixed(2)}/sem`;
      break;

    case "monthly":
      // Preço mensal × quantidade de meses
      total = basePrice * qty;
      breakdown = `${qty} meses × R$${basePrice.toFixed(2)}/mês`;
      break;

    case "quarterly":
      // Preço trimestral × quantidade de trimestres
      total = basePrice * qty;
      breakdown = `${qty} trim × R$${basePrice.toFixed(2)}/trim`;
      break;

    case "semiannual":
      // Preço semestral × quantidade de semestres
      total = basePrice * qty;
      breakdown = `${qty} sem × R$${basePrice.toFixed(2)}/sem`;
      break;

    case "yearly":
      // Preço anual × quantidade de anos
      total = basePrice * qty;
      breakdown = `${qty} anos × R$${basePrice.toFixed(2)}/ano`;
      break;

    case "hourly_billed_daily":
      // Preço por hora, cobra por dia: horas/dia × preço × dias
      total = hoursPerDay * basePrice * qty;
      breakdown = `${qty} dias × ${hoursPerDay}h/dia × R$${basePrice.toFixed(2)}/h`;
      break;

    case "hourly_billed_monthly":
      // Preço por hora, cobra mensal: horas/dia × dias/mês × preço × meses
      total = hoursPerDay * daysPerMonth * basePrice * months;
      breakdown = `${months} meses × ${daysPerMonth}d × ${hoursPerDay}h × R$${basePrice.toFixed(2)}/h`;
      break;

    case "daily_billed_monthly":
      // Preço por dia, cobra mensal: dias/mês × preço × meses
      total = daysPerMonth * basePrice * months;
      breakdown = `${months} meses × ${daysPerMonth}d × R$${basePrice.toFixed(2)}/dia`;
      break;

    case "monthly_billed_yearly":
      // Preço mensal, cobra anual: 12 × preço × anos
      total = 12 * basePrice * qty;
      breakdown = `${qty} anos × 12 meses × R$${basePrice.toFixed(2)}/mês`;
      break;

    case "on_demand":
    case "one_time":
    default:
      // Pagamento único ou sob demanda: quantidade × preço
      total = qty * basePrice;
      breakdown = `${qty} × R$${basePrice.toFixed(2)}`;
      break;
  }

  // Aplicar desconto
  if (item.discount > 0) {
    const discountValue =
      item.discount_type === "percent"
        ? total * (item.discount / 100)
        : item.discount;
    total -= discountValue;
  }

  return { total: Math.max(0, total), breakdown };
};

export default function BudgetFormPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const isEditing = !!id;

  const {
    register,
    handleSubmit,
    control,
    reset,
    formState: { errors },
    watch,
    setValue,
  } = useForm<BudgetFormData>({
    defaultValues: {
      client_id: "",
      title: "",
      description: "",
      valid_until: "",
      payment_terms: "",
      delivery_time: "",
      warranty: "",
      notes: "",
      internal_notes: "",
      discount: 0,
      discount_type: "percent",
      items: [],
    },
  });

  const { fields, append, remove } = useFieldArray({
    control,
    name: "items",
  });

  const watchItems = watch("items");
  const watchDiscount = watch("discount");
  const watchDiscountType = watch("discount_type");

  // Fetch data
  const { data: budgetData, isLoading: isLoadingBudget } = useQuery({
    queryKey: ["budget", id],
    queryFn: () => budgetsApi.get(id!),
    enabled: isEditing,
  });

  const { data: clientsData } = useQuery({
    queryKey: ["clients-all"],
    queryFn: () => clientsApi.list({ per_page: 1000 }),
  });

  const { data: productsData } = useQuery({
    queryKey: ["products-all"],
    queryFn: () => productsApi.list({ per_page: 1000 }),
  });

  const clients = clientsData?.data?.items || [];
  const products = productsData?.data?.items || [];

  useEffect(() => {
    if (budgetData?.data) {
      const budget = budgetData.data;
      reset({
        client_id: budget.client_id || "",
        title: budget.title || "",
        description: budget.description || "",
        valid_until: budget.validity_date
          ? budget.validity_date.split("T")[0]
          : "",
        payment_terms: budget.payment_conditions || "",
        delivery_time: budget.delivery_time || "",
        warranty: budget.warranty || "",
        notes: budget.notes || "",
        internal_notes: budget.internal_notes || "",
        discount: budget.discount_value || 0,
        discount_type: budget.discount_type || "percent",
        items:
          budget.items?.map((item: any) => ({
            product_id: item.product_id || "",
            name: item.name || "",
            description: item.description || "",
            detailed_description: item.detailed_description || "",
            quantity: item.quantity || 1,
            unit: item.unit || "un",
            unit_price: item.unit_price || 0,
            discount: item.discount_value || 0,
            discount_type: item.discount_type || "percent",
            billing_type: item.billing_type || "one_time",
            hours_per_day: item.hours_per_day || 8,
            days_per_month: item.days_per_month || 22,
            months: item.months || 1,
          })) || [],
      });
    }
  }, [budgetData, reset]);

  const createMutation = useMutation({
    mutationFn: budgetsApi.create,
    onSuccess: (data) => {
      toast.success("Orçamento criado com sucesso");
      queryClient.invalidateQueries({ queryKey: ["budgets"] });
      navigate(`/admin/budgets/${data.data?.id || ""}`);
    },
    onError: (error: any) => {
      toast.error(error?.response?.data?.message || "Erro ao criar orçamento");
    },
  });

  const updateMutation = useMutation({
    mutationFn: (data: BudgetFormData) => budgetsApi.update(id!, data),
    onSuccess: () => {
      toast.success("Orçamento atualizado com sucesso");
      queryClient.invalidateQueries({ queryKey: ["budgets"] });
      queryClient.invalidateQueries({ queryKey: ["budget", id] });
      navigate(`/admin/budgets/${id}`);
    },
    onError: (error: any) => {
      toast.error(
        error?.response?.data?.message || "Erro ao atualizar orçamento",
      );
    },
  });

  const isLoading = createMutation.isPending || updateMutation.isPending;

  // Calculate totals
  const totals = useMemo(() => {
    let subtotal = 0;

    watchItems.forEach((item) => {
      const { total } = calculateItemTotal(item);
      subtotal += total;
    });

    let totalDiscount = 0;
    if (watchDiscount > 0) {
      totalDiscount =
        watchDiscountType === "percent"
          ? subtotal * (watchDiscount / 100)
          : watchDiscount;
    }

    const total = subtotal - totalDiscount;

    return {
      subtotal,
      discount: totalDiscount,
      total: Math.max(0, total),
    };
  }, [watchItems, watchDiscount, watchDiscountType]);

  const onSubmit = (data: BudgetFormData) => {
    if (data.items.length === 0) {
      toast.error("Adicione pelo menos um item ao orçamento");
      return;
    }

    // Mapear campos do formulário para o schema do backend
    const payload = {
      client_id: data.client_id,
      title: data.title || undefined,
      validity_date: data.valid_until || undefined,
      notes: data.notes || undefined,
      terms: data.warranty || undefined,
      payment_conditions: data.payment_terms || undefined,
      discount_type: data.discount_type,
      discount_value: Number(data.discount) || 0,
      items: data.items.map((item) => ({
        product_id: item.product_id || undefined,
        name: item.name || item.description || "Item",
        description: item.description || undefined,
        detailed_description: item.detailed_description || undefined,
        unit: item.unit || "un",
        quantity: Number(item.quantity) || 1,
        unit_price: Number(item.unit_price) || 0,
        discount_type: item.discount_type,
        discount_value: Number(item.discount) || 0,
        billing_type: item.billing_type || "one_time",
        hours_per_day: Number(item.hours_per_day) || 8,
        days_per_month: Number(item.days_per_month) || 22,
        months: Number(item.months) || 1,
        calculated_total: calculateItemTotal(item).total,
      })),
    };

    if (isEditing) {
      updateMutation.mutate(payload as any);
    } else {
      createMutation.mutate(payload);
    }
  };

  const addItem = () => {
    append({
      product_id: "",
      name: "",
      description: "",
      detailed_description: "",
      quantity: 1,
      unit: "un",
      unit_price: 0,
      discount: 0,
      discount_type: "percent",
      billing_type: "one_time",
      hours_per_day: 8,
      days_per_month: 22,
      months: 1,
    });
  };

  const handleProductSelect = (index: number, productId: string) => {
    const product = products.find((p: any) => p.id === productId);
    if (product) {
      setValue(`items.${index}.name`, product.name || "");
      setValue(
        `items.${index}.description`,
        product.description || product.name,
      );
      setValue(
        `items.${index}.detailed_description`,
        product.detailed_description || "",
      );
      setValue(`items.${index}.unit`, product.unit || "un");
      setValue(`items.${index}.unit_price`, product.price || 0);
      setValue(
        `items.${index}.billing_type`,
        product.billing_type || "one_time",
      );
    }
  };

  // Verifica se o tipo de cobrança precisa de campos extras
  const needsHoursPerDay = (billingType: string) =>
    ["hourly_billed_daily", "hourly_billed_monthly"].includes(billingType);

  const needsDaysPerMonth = (billingType: string) =>
    ["hourly_billed_monthly", "daily_billed_monthly"].includes(billingType);

  const needsMonths = (billingType: string) =>
    ["hourly_billed_monthly", "daily_billed_monthly"].includes(billingType);

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat("pt-BR", {
      style: "currency",
      currency: "BRL",
    }).format(value);
  };

  if (isEditing && isLoadingBudget) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-2 border-primary border-t-transparent"></div>
      </div>
    );
  }

  return (
    <div className="space-y-4 sm:space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex items-start sm:items-center gap-3 sm:gap-4">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate("/admin/budgets")}
          className="flex-shrink-0"
        >
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <div>
          <h1 className="text-xl sm:text-3xl font-bold text-text-primary">
            {isEditing ? "Editar Orçamento" : "Novo Orçamento"}
          </h1>
          <p className="text-text-secondary mt-1 text-sm sm:text-base">
            {isEditing
              ? "Atualize as informações do orçamento"
              : "Crie um novo orçamento"}
          </p>
        </div>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
        {/* Client & Basic Info */}
        <Card className="p-6">
          <h2 className="text-lg font-semibold text-text-primary mb-4">
            Informações do Orçamento
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Select
              label="Cliente *"
              {...register("client_id", { required: "Selecione um cliente" })}
              error={errors.client_id?.message}
              options={[
                { value: "", label: "Selecione um cliente" },
                ...clients.map((c: any) => ({ value: c.id, label: c.name })),
              ]}
            />
            <Input
              label="Válido até"
              type="date"
              {...register("valid_until")}
            />
            <div className="md:col-span-2">
              <Input
                label="Título"
                {...register("title")}
                placeholder="Ex: Proposta de redesign do site"
              />
            </div>
            <div className="md:col-span-2">
              <Textarea
                label="Descrição"
                {...register("description")}
                placeholder="Descrição detalhada do orçamento..."
                rows={3}
              />
            </div>
          </div>
        </Card>

        {/* Items */}
        <Card className="p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-text-primary">
              Itens do Orçamento
            </h2>
            <Button
              type="button"
              variant="secondary"
              size="sm"
              onClick={addItem}
            >
              <Plus className="w-4 h-4 mr-2" />
              Adicionar Item
            </Button>
          </div>

          {fields.length === 0 ? (
            <div className="text-center py-12 border-2 border-dashed border-white/10 rounded-lg">
              <Calculator className="w-12 h-12 text-text-muted mx-auto mb-4" />
              <p className="text-text-muted mb-4">
                Nenhum item adicionado. Clique em "Adicionar Item" para começar.
              </p>
              <Button type="button" variant="secondary" onClick={addItem}>
                <Plus className="w-4 h-4 mr-2" />
                Adicionar Primeiro Item
              </Button>
            </div>
          ) : (
            <div className="space-y-4">
              {fields.map((field, index) => (
                <div
                  key={field.id}
                  className="p-3 sm:p-4 bg-dark-lighter rounded-lg border border-white/5"
                >
                  <div className="flex flex-col sm:flex-row sm:items-start gap-3 sm:gap-4">
                    <div className="flex-1 grid grid-cols-2 sm:grid-cols-1 md:grid-cols-12 gap-3 sm:gap-4">
                      {/* Product Select */}
                      <div className="col-span-2 sm:col-span-1 md:col-span-4">
                        <Controller
                          control={control}
                          name={`items.${index}.product_id`}
                          render={({ field: { onChange, value } }) => (
                            <Select
                              label="Produto"
                              value={value}
                              onChange={(e) => {
                                onChange(e);
                                handleProductSelect(index, e.target.value);
                              }}
                              options={[
                                {
                                  value: "",
                                  label: "Selecione ou digite manualmente",
                                },
                                ...products.map((p: any) => ({
                                  value: p.id,
                                  label: p.name,
                                })),
                              ]}
                            />
                          )}
                        />
                      </div>

                      {/* Description */}
                      <div className="col-span-2 sm:col-span-1 md:col-span-8">
                        <Input
                          label="Descrição *"
                          {...register(`items.${index}.description`, {
                            required: true,
                          })}
                          placeholder="Descrição do item"
                        />
                      </div>

                      {/* Quantity */}
                      <div className="col-span-1 md:col-span-2">
                        <Input
                          label="Qtd"
                          type="number"
                          step="0.01"
                          min="0.01"
                          {...register(`items.${index}.quantity`, {
                            required: true,
                            valueAsNumber: true,
                          })}
                        />
                      </div>

                      {/* Unit */}
                      <div className="col-span-1 md:col-span-2">
                        <Input
                          label="Unidade"
                          {...register(`items.${index}.unit`)}
                          placeholder="un"
                        />
                      </div>

                      {/* Unit Price */}
                      <div className="col-span-1 md:col-span-2">
                        <Input
                          label="Preço Unit."
                          type="number"
                          step="0.01"
                          min="0"
                          {...register(`items.${index}.unit_price`, {
                            required: true,
                            valueAsNumber: true,
                          })}
                        />
                      </div>

                      {/* Billing Type */}
                      <div className="col-span-1 md:col-span-2">
                        <Select
                          label="Cobrança"
                          {...register(`items.${index}.billing_type`)}
                          options={BILLING_TYPES}
                        />
                      </div>

                      {/* Conditional fields based on billing type */}
                      {needsHoursPerDay(watchItems[index]?.billing_type) && (
                        <div className="col-span-1 md:col-span-1">
                          <Input
                            label="H/Dia"
                            type="number"
                            step="1"
                            min="1"
                            max="24"
                            {...register(`items.${index}.hours_per_day`, {
                              valueAsNumber: true,
                            })}
                          />
                        </div>
                      )}

                      {needsDaysPerMonth(watchItems[index]?.billing_type) && (
                        <div className="col-span-1 md:col-span-1">
                          <Input
                            label="D/Mês"
                            type="number"
                            step="1"
                            min="1"
                            max="31"
                            {...register(`items.${index}.days_per_month`, {
                              valueAsNumber: true,
                            })}
                          />
                        </div>
                      )}

                      {needsMonths(watchItems[index]?.billing_type) && (
                        <div className="col-span-1 md:col-span-1">
                          <Input
                            label="Meses"
                            type="number"
                            step="1"
                            min="1"
                            {...register(`items.${index}.months`, {
                              valueAsNumber: true,
                            })}
                          />
                        </div>
                      )}

                      {/* Discount */}
                      <div className="col-span-1 md:col-span-2">
                        <label className="block text-sm font-medium text-text-secondary mb-2">
                          Desconto
                        </label>
                        <div className="flex gap-2">
                          <Input
                            type="number"
                            step="0.01"
                            min="0"
                            {...register(`items.${index}.discount`, {
                              valueAsNumber: true,
                            })}
                            className="flex-1"
                          />
                          <Select
                            {...register(`items.${index}.discount_type`)}
                            options={DISCOUNT_TYPES}
                            className="w-16"
                          />
                        </div>
                      </div>

                      {/* Item Total */}
                      <div className="col-span-2 md:col-span-2">
                        <label className="block text-sm font-medium text-text-secondary mb-2">
                          Total
                        </label>
                        <div className="h-11 px-3 flex flex-col justify-center bg-dark border border-white/10 rounded-lg">
                          <span className="font-mono text-primary text-sm">
                            {formatCurrency(
                              watchItems[index]
                                ? calculateItemTotal(watchItems[index]).total
                                : 0,
                            )}
                          </span>
                          <span
                            className="text-[10px] text-text-muted truncate"
                            title={
                              watchItems[index]
                                ? calculateItemTotal(watchItems[index])
                                    .breakdown
                                : ""
                            }
                          >
                            {watchItems[index]
                              ? calculateItemTotal(watchItems[index]).breakdown
                              : ""}
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* Remove Button */}
                    <button
                      type="button"
                      onClick={() => remove(index)}
                      className="self-end sm:self-auto sm:mt-8 p-2 text-text-muted hover:text-red-400 hover:bg-red-500/10 rounded-lg transition-colors flex-shrink-0"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Totals */}
          {fields.length > 0 && (
            <div className="mt-6 pt-6 border-t border-white/10">
              <div className="flex flex-col items-stretch sm:items-end gap-3">
                <div className="flex items-center justify-between sm:justify-end sm:gap-4">
                  <span className="text-text-secondary">Subtotal:</span>
                  <span className="font-mono text-base sm:text-lg">
                    {formatCurrency(totals.subtotal)}
                  </span>
                </div>
                <div className="flex flex-wrap items-center justify-between sm:justify-end gap-2 sm:gap-4">
                  <span className="text-text-secondary">Desconto Geral:</span>
                  <div className="flex gap-2">
                    <Input
                      type="number"
                      step="0.01"
                      min="0"
                      {...register("discount", { valueAsNumber: true })}
                      className="w-20 sm:w-24"
                    />
                    <Select
                      {...register("discount_type")}
                      options={DISCOUNT_TYPES}
                      className="w-16 sm:w-20"
                    />
                  </div>
                  <span className="font-mono text-red-400">
                    -{formatCurrency(totals.discount)}
                  </span>
                </div>
                <div className="flex items-center justify-between sm:justify-end sm:gap-4 pt-2 border-t border-white/10 w-full sm:w-auto">
                  <span className="text-base sm:text-lg font-semibold text-text-primary">
                    Total:
                  </span>
                  <span className="font-mono text-xl sm:text-2xl text-primary font-bold">
                    {formatCurrency(totals.total)}
                  </span>
                </div>
              </div>
            </div>
          )}
        </Card>

        {/* Terms & Conditions */}
        <Card className="p-6">
          <h2 className="text-lg font-semibold text-text-primary mb-4">
            Condições
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Textarea
              label="Condições de Pagamento"
              {...register("payment_terms")}
              placeholder="Ex: 50% na aprovação, 50% na entrega"
              rows={3}
            />
            <Textarea
              label="Prazo de Entrega"
              {...register("delivery_time")}
              placeholder="Ex: 15 dias úteis após aprovação"
              rows={3}
            />
            <Textarea
              label="Garantia"
              {...register("warranty")}
              placeholder="Ex: 90 dias para suporte técnico"
              rows={3}
            />
          </div>
        </Card>

        {/* Notes */}
        <Card className="p-6">
          <h2 className="text-lg font-semibold text-text-primary mb-4">
            Observações
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Textarea
              label="Observações para o Cliente"
              {...register("notes")}
              placeholder="Estas observações aparecerão no orçamento"
              rows={4}
            />
            <Textarea
              label="Observações Internas"
              {...register("internal_notes")}
              placeholder="Apenas para uso interno, não aparece no orçamento"
              rows={4}
            />
          </div>
        </Card>

        {/* Actions */}
        <div className="flex flex-col-reverse sm:flex-row items-stretch sm:items-center justify-end gap-3 sm:gap-4 pb-4">
          <Button
            type="button"
            variant="secondary"
            onClick={() => navigate("/admin/budgets")}
            className="w-full sm:w-auto"
          >
            Cancelar
          </Button>
          <Button
            type="submit"
            isLoading={isLoading}
            className="w-full sm:w-auto"
          >
            {isEditing ? "Salvar Alterações" : "Criar Orçamento"}
          </Button>
        </div>
      </form>
    </div>
  );
}
